/**
 * work-hours-lib.ts — Phần THUẦN của "Giờ làm & chế độ" của một nhân viên AI (dùng chung: màn nhân viên AI, luồng tạo mới).
 *
 * Máy chủ lưu KHUNG NGOÀI GIỜ: `scheduleStartHour` (mặc định 18) → `scheduleEndHour` (8);
 * `scheduleDaytimeMode` ∈ suggest | manual = chế độ TRONG giờ làm; `scheduleNighttimeMode` ∈ auto | suggest = NGOÀI giờ.
 * Giao diện nói theo GIỜ LÀM VIỆC: "08:00–18:00" = endHour → startHour. Không bật lịch → `defaultMode` cả ngày.
 * Mặc định Q4: trong giờ Gợi ý, ngoài giờ Tự động.
 */
import type { AiMode } from '@/hooks/use-ai'
import { AI_MODE_LABEL, isAfterHoursAt } from '@/lib/ai-khung'
import { i18n } from '@/lib/i18n'

export interface ScheduleValue {
  scheduleEnabled: boolean
  scheduleStartHour: number | null
  scheduleEndHour: number | null
  scheduleDaytimeMode: string | null
  scheduleNighttimeMode: string | null
  defaultMode: AiMode
}

export type DaytimeMode = 'suggest' | 'manual'
export type NighttimeMode = 'auto' | 'suggest'

/** Q4 — nhân viên AI mới: trong giờ 08:00–18:00 Gợi ý, ngoài giờ Tự động. */
export const Q4_SCHEDULE: ScheduleValue = {
  scheduleEnabled: true,
  scheduleStartHour: 18,
  scheduleEndHour: 8,
  scheduleDaytimeMode: 'suggest',
  scheduleNighttimeMode: 'auto',
  defaultMode: 'suggest',
}

export const HOURS = Array.from({ length: 24 }, (_, h) => h)

export const formatHour = (h: number) => `${String(((h % 24) + 24) % 24).padStart(2, '0')}:00`

const hourOr = (v: number | null | undefined, d: number) => (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 23 ? v : d)

/** Khung ngoài giờ của máy chủ → giờ làm việc hiển thị: `from` = endHour, `to` = startHour. */
export function workHoursFromSchedule(startHour: number | null | undefined, endHour: number | null | undefined): { from: number; to: number } {
  return { from: hourOr(endHour, 8), to: hourOr(startHour, 18) }
}

/** Giờ làm việc người dùng chọn → khung ngoài giờ lưu ở máy chủ. */
export function scheduleFromWorkHours(from: number, to: number): { scheduleStartHour: number; scheduleEndHour: number } {
  return { scheduleStartHour: to, scheduleEndHour: from }
}

/** "08:00–18:00". */
export function workHoursText(v: Pick<ScheduleValue, 'scheduleStartHour' | 'scheduleEndHour'>): string {
  const w = workHoursFromSchedule(v.scheduleStartHour, v.scheduleEndHour)
  return `${formatHour(w.from)}–${formatHour(w.to)}`
}

export const asDaytime = (v: unknown): DaytimeMode => (v === 'manual' ? 'manual' : 'suggest')
export const asNighttime = (v: unknown): NighttimeMode => (v === 'suggest' ? 'suggest' : 'auto')
const asMode = (v: unknown, d: AiMode): AiMode => (v === 'manual' || v === 'suggest' || v === 'auto' ? v : d)

/** Cách đặt lịch: 2 mẫu nhanh, tuỳ chỉnh giờ & chế độ, một chế độ cả ngày. */
export type ScheduleKind = 'q4' | 'auto247' | 'custom' | 'single'

export const SCHEDULE_KINDS: Array<{ id: ScheduleKind; label: string; hint: string }> = [
  { id: 'q4', get label() { return i18n.t('aiComp:workHours.kind.q4.label') }, get hint() { return i18n.t('aiComp:workHours.kind.q4.hint') } },
  { id: 'auto247', get label() { return i18n.t('aiComp:workHours.kind.auto247.label') }, get hint() { return i18n.t('aiComp:workHours.kind.auto247.hint') } },
  { id: 'custom', get label() { return i18n.t('aiComp:workHours.kind.custom.label') }, get hint() { return i18n.t('aiComp:workHours.kind.custom.hint') } },
  { id: 'single', get label() { return i18n.t('aiComp:workHours.kind.single.label') }, get hint() { return i18n.t('aiComp:workHours.kind.single.hint') } },
]

/** Lịch hiện tại thuộc cách đặt nào. */
export function scheduleKindOf(v: ScheduleValue): ScheduleKind {
  if (!v.scheduleEnabled) return v.defaultMode === 'auto' ? 'auto247' : 'single'
  const isQ4 = hourOr(v.scheduleStartHour, 18) === 18 && hourOr(v.scheduleEndHour, 8) === 8
    && asDaytime(v.scheduleDaytimeMode) === 'suggest' && asNighttime(v.scheduleNighttimeMode) === 'auto'
  return isQ4 ? 'q4' : 'custom'
}

/**
 * Chọn một cách đặt → các trường gửi máy chủ. Tuỳ chỉnh giữ giờ/chế độ đang có (bật lịch); một chế độ cả ngày giữ
 * `defaultMode` nếu không phải Tự động (Tự động = mẫu 24/7) → mặc định Gợi ý.
 */
export function applyScheduleKind(kind: ScheduleKind, cur: ScheduleValue): ScheduleValue {
  switch (kind) {
    case 'q4': return { ...cur, ...Q4_SCHEDULE, defaultMode: cur.defaultMode }
    case 'auto247': return { ...cur, scheduleEnabled: false, defaultMode: 'auto' }
    case 'single': return { ...cur, scheduleEnabled: false, defaultMode: cur.defaultMode === 'auto' ? 'suggest' : cur.defaultMode }
    case 'custom': return {
      ...cur,
      scheduleEnabled: true,
      scheduleStartHour: hourOr(cur.scheduleStartHour, 18),
      scheduleEndHour: hourOr(cur.scheduleEndHour, 8),
      scheduleDaytimeMode: asDaytime(cur.scheduleDaytimeMode),
      scheduleNighttimeMode: asNighttime(cur.scheduleNighttimeMode),
    }
  }
}

/** "Giờ làm việc 08:00–18:00: Gợi ý · ngoài giờ: Tự động" / "Tự động cả ngày". */
export function scheduleSummary(v: ScheduleValue): string {
  if (!v.scheduleEnabled) return i18n.t('aiComp:workHours.allDayMode', { mode: AI_MODE_LABEL[asMode(v.defaultMode, 'suggest')] })
  return i18n.t('aiComp:workHours.summary', {
    hours: workHoursText(v), day: AI_MODE_LABEL[asDaytime(v.scheduleDaytimeMode)], night: AI_MODE_LABEL[asNighttime(v.scheduleNighttimeMode)],
  })
}

/** Chế độ theo lịch LÚC NÀY (chưa tính công tắc tổ chức / bot) — giống backend `botModeNow`. */
export function scheduleModeNow(v: ScheduleValue, now: Date = new Date(), timeZone = 'Asia/Ho_Chi_Minh'): { mode: AiMode; window: 'in-hours' | 'after-hours' | 'all-day' } {
  if (!v.scheduleEnabled) return { mode: asMode(v.defaultMode, 'suggest'), window: 'all-day' }
  const after = isAfterHoursAt(now, hourOr(v.scheduleStartHour, 18), hourOr(v.scheduleEndHour, 8), timeZone)
  return after
    ? { mode: asNighttime(v.scheduleNighttimeMode), window: 'after-hours' }
    : { mode: asDaytime(v.scheduleDaytimeMode), window: 'in-hours' }
}

/** Kiểm giờ làm việc: hai mốc trùng nhau = không có giờ làm (máy chủ coi như không có khung ngoài giờ). */
export function workHoursProblem(v: ScheduleValue): string | null {
  if (!v.scheduleEnabled) return null
  const w = workHoursFromSchedule(v.scheduleStartHour, v.scheduleEndHour)
  return w.from === w.to ? i18n.t('aiComp:workHours.problemSame') : null
}
